import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../../services/api';
import { chartSeries, fitColors, tokens } from './marketingTokens';
import './ai-vis.css';

const PAGE_SIZE = 20;

const LINE_COLORS = chartSeries;

/* Match exactly the Home page "By Service" colors */
const SERVICE_NAME_TO_KEY = {
  ChatGPT: 'chatgpt',
  Perplexity: 'perplexity',
  'Google AI Overviews': 'google_ai_overviews',
};

const SERVICE_BAR_COLORS = {
  chatgpt: '#212121',
  perplexity: '#4c8f99',
  google_ai_overviews: '#fabb06',
};

function serviceIconHref(key) {
  if (key === 'chatgpt') return '/chatgpt-icon.png';
  if (key === 'perplexity') return '/perplexity-icon.png';
  if (key === 'google_ai_overviews') return '/google-icon.png';
  return null;
}

/** Custom X-axis tick: icon above label */
function ServiceXTick({ x, y, payload }) {
  const key = SERVICE_NAME_TO_KEY[payload.value];
  const logoSrc = key ? serviceIconHref(key) : null;
  const color = SERVICE_BAR_COLORS[key] || 'var(--aivis-text-subtle)';
  return (
    <g transform={`translate(${x},${y})`}>
      {logoSrc && (
        <image href={logoSrc} x={-12} y={6} width={24} height={24} preserveAspectRatio="xMidYMid meet" />
      )}
      <text
        x={0}
        y={logoSrc ? 38 : 16}
        textAnchor="middle"
        fontSize={11}
        fontWeight={600}
        fill={color}
      >
        {payload.value}
      </text>
    </g>
  );
}

const COL_TOOLTIPS = {
  times_cited: 'Total number of AI chat responses that cited this URL for this prompt across all campaign runs.',
};

const FIT_COLORS = fitColors;

/* ─── Helpers ─── */
function formatShortDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return iso; }
}

function relativeTime(iso) {
  if (!iso) return '—';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 2) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return `${Math.floor(days / 30)}mo ago`;
  } catch { return '—'; }
}

function absoluteDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return '—'; }
}

function urlDisplayFull(url, maxLen = 70) {
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    const full = u.hostname + u.pathname + (u.search || '');
    return full.length > maxLen ? `${full.slice(0, maxLen)}…` : full;
  } catch {
    return url.length > maxLen ? `${url.slice(0, maxLen)}…` : url;
  }
}

function typeClass(classifier) {
  const map = {
    reddit: 'aivis-dash-type--ugc',
    product_roundup: 'aivis-dash-type--editorial',
    review: 'aivis-dash-type--review',
    ecommerce: 'aivis-dash-type--commerce',
    review_site: 'aivis-dash-type--reviewsite',
    client: 'aivis-dash-type--you',
    homepage: 'aivis-dash-type--corp',
    video: 'aivis-dash-type--video',
    other: 'aivis-dash-type--other',
  };
  return `aivis-dash-type ${map[classifier] || 'aivis-dash-type--other'}`;
}

/* ─── Tooltip portal ─── */
const TOOLTIP_W = 230;
const TOOLTIP_MARGIN = 12;

function TooltipPortal({ text, anchorRef, visible }) {
  const [style, setStyle] = useState({});
  useEffect(() => {
    if (!visible || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const anchorCX = r.left + r.width / 2;
    let left = anchorCX - TOOLTIP_W / 2;
    left = Math.max(TOOLTIP_MARGIN, Math.min(left, window.innerWidth - TOOLTIP_W - TOOLTIP_MARGIN));
    const arrowLeft = Math.round(Math.max(14, Math.min(anchorCX - left, TOOLTIP_W - 14)));
    setStyle({ left, top: r.top - 10, transform: 'translateY(-100%)', '--arrow-left': `${arrowLeft}px` });
  }, [visible, anchorRef]);
  if (!visible || !text) return null;
  return createPortal(
    <div className="aivis-domains-tooltip-popup" style={style} role="tooltip">{text}</div>,
    document.body
  );
}

function InfoTooltip({ text }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  return (
    <span ref={ref} className="aivis-domains-th-info" aria-label={text} role="img"
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)} onBlur={() => setVisible(false)} tabIndex={0}
    >
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 7v5M8 5.25V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <TooltipPortal text={text} anchorRef={ref} visible={visible} />
    </span>
  );
}

function SortIcon({ active, dir }) {
  if (active) {
    return (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden
        className="aivis-domains-sort-icon aivis-domains-sort-icon--active">
        {dir === 'asc' ? <path d="M6 2L10.5 9H1.5L6 2Z" fill="currentColor" /> : <path d="M6 10L1.5 3H10.5L6 10Z" fill="currentColor" />}
      </svg>
    );
  }
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden className="aivis-domains-sort-icon">
      <path d="M3.5 5L6 2L8.5 5H3.5Z" fill="currentColor" opacity="0.4" />
      <path d="M3.5 7L6 10L8.5 7H3.5Z" fill="currentColor" opacity="0.4" />
    </svg>
  );
}

function ColHeader({ label, sortKey, currentSort, currentDir, onSort, tooltip, numeric }) {
  const active = currentSort === sortKey;
  return (
    <th scope="col"
      className={`${numeric ? 'aivis-domains-th-num' : ''} ${sortKey ? 'aivis-domains-th-sortable' : ''}`}
      onClick={sortKey ? () => onSort(sortKey) : undefined}
      aria-sort={active ? (currentDir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <div className="aivis-domains-th-inner">
        <span className="aivis-domains-th-label">{label}</span>
        {sortKey && <SortIcon active={active} dir={currentDir} />}
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
    </th>
  );
}

function PaginationControls({ page, totalPages, totalItems, onPage }) {
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push('…');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('…');
    pages.push(totalPages);
  }
  return (
    <div className="aivis-domains-pagination">
      <div className="aivis-domains-pg-controls">
        <button type="button" className="aivis-domains-pg-btn" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous">‹</button>
        {pages.map((p, i) =>
          p === '…'
            ? <span key={`e${i}`} className="aivis-domains-pg-ellipsis">…</span>
            : <button key={p} type="button" className={`aivis-domains-pg-btn ${p === page ? 'aivis-domains-pg-btn--active' : ''}`} onClick={() => onPage(p)}>{p}</button>
        )}
        <button type="button" className="aivis-domains-pg-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)} aria-label="Next">›</button>
      </div>
      <span className="aivis-domains-pg-count">{totalItems.toLocaleString()} item{totalItems === 1 ? '' : 's'}</span>
    </div>
  );
}

/* ─── Main Page ─── */
export default function SourceDetailPage() {
  const [searchParams] = useSearchParams();
  const url = searchParams.get('url') || '';
  const { selectedCampaignId } = useOutletContext();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [promptPage, setPromptPage] = useState(1);
  const [sortKey, setSortKey] = useState('times_cited');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    setData(null); setError(''); setPromptPage(1);
  }, [selectedCampaignId, url]);

  useEffect(() => {
    if (!selectedCampaignId || !url) return undefined;
    let cancelled = false;
    setLoading(true); setError('');
    api.get(`/aivis/campaigns/${selectedCampaignId}/sources/detail/`, { params: { url } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || 'Could not load source details.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedCampaignId, url]);

  function handleSort(key) {
    if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
    setPromptPage(1);
  }

  const stats = data?.source_stats;

  /* ── Line chart data ── */
  const lineData = useMemo(
    () => (data?.series || []).map((row) => ({ ...row, label: formatShortDate(row.date) })),
    [data?.series]
  );

  /* ── Bar chart data ── */
  const barData = useMemo(() => {
    return Object.entries(data?.by_service || {}).map(([service, count]) => {
      const key = SERVICE_NAME_TO_KEY[service] || '';
      return { service, count, fill: SERVICE_BAR_COLORS[key] || 'var(--aivis-text-subtle)' };
    });
  }, [data?.by_service]);

  /* ── Prompts table ── */
  const sortedPrompts = useMemo(() => {
    const arr = [...(data?.prompts || [])];
    arr.sort((a, b) => {
      const av = a[sortKey] ?? ''; const bv = b[sortKey] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      const as = String(av).toLowerCase(); const bs = String(bv).toLowerCase();
      if (as < bs) return sortDir === 'asc' ? -1 : 1;
      if (as > bs) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [data?.prompts, sortKey, sortDir]);

  const totalPrompts = sortedPrompts.length;
  const totalPromptPages = Math.max(1, Math.ceil(totalPrompts / PAGE_SIZE));
  const safePromptPage = Math.min(promptPage, totalPromptPages);
  const pagePrompts = sortedPrompts.slice((safePromptPage - 1) * PAGE_SIZE, safePromptPage * PAGE_SIZE);

  /* ── Brands ── */

  const renderLineTooltip = useCallback(({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="aivis-dash-tooltip">
        <div className="aivis-dash-tooltip-title">{label}</div>
        {payload.map((p) => (
          <div key={p.dataKey} className="aivis-dash-tooltip-row">
            <span className="aivis-dash-tooltip-dot" style={{ background: p.color }} />
            <span>Citations</span>
            <strong>{p.value}</strong>
          </div>
        ))}
      </div>
    );
  }, []);

  const renderBarTooltip = useCallback(({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0];
    const key = SERVICE_NAME_TO_KEY[label] || '';
    const accent = SERVICE_BAR_COLORS[key] || p.fill || 'var(--aivis-text-subtle)';
    return (
      <div className="aivis-dash-service-tooltip" style={{ borderLeftColor: accent }}>
        <div className="aivis-dash-service-tooltip-label">{label}</div>
        <div className="aivis-dash-service-tooltip-value">
          <span style={{ color: accent }}>{p.value}</span>
          <span className="aivis-dash-service-tooltip-sublabel">citation{p.value === 1 ? '' : 's'}</span>
        </div>
      </div>
    );
  }, []);

  if (!url) {
    return (
      <div className="aivis-domains-page">
        <p className="aivis-dash-empty">No source URL specified.</p>
      </div>
    );
  }

  return (
    <div className="aivis-domains-page">
      {/* Breadcrumb */}
      <nav className="aivis-domain-detail-breadcrumb" aria-label="Breadcrumb">
        <Link to="/ai-vis/sources" className="aivis-domain-detail-bc-link">Cited URLs</Link>
        <span className="aivis-domain-detail-bc-sep" aria-hidden>›</span>
        <span className="aivis-domain-detail-bc-current">
          {stats?.page_title || urlDisplayFull(url, 60)}
        </span>
      </nav>

      {error ? <div className="aivis-auth-error">{error}</div> : null}
      {loading && !data ? <p className="aivis-dash-empty" style={{ padding: '40px 0' }}>Loading…</p> : null}

      {/* ── Header ── */}
      {stats && (
        <header className="aivis-source-detail-header">
          <div className="aivis-source-detail-title-row">
            <div className="aivis-source-detail-title-block">
              {stats.page_title && (
                <h1 className="aivis-source-detail-title">{stats.page_title}</h1>
              )}
              <a
                href={url.startsWith('http') ? url : `https://${url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="aivis-source-detail-url-link"
                title={url}
              >
                {urlDisplayFull(url, 90)}
              </a>
            </div>
            <div className="aivis-source-detail-badges">
              {stats.type_label && (
                <span className={typeClass(stats.classifier)}>{stats.type_label}</span>
              )}
              {stats.url_type_label && stats.url_type_label !== stats.type_label && (
                <span className={typeClass(stats.classifier)}>{stats.url_type_label}</span>
              )}
            </div>
          </div>

          <div className="aivis-source-detail-stats-row">
            <div className="aivis-source-detail-stat">
              <span className="aivis-source-detail-stat-label">Times Cited</span>
              <span className="aivis-source-detail-stat-value">{stats.times_cited}</span>
            </div>
            <div className="aivis-source-detail-stat-divider" aria-hidden />
            <div className="aivis-source-detail-stat">
              <span className="aivis-source-detail-stat-label">No. of prompts</span>
              <span className="aivis-source-detail-stat-value">{stats.num_prompts}</span>
            </div>
            <div className="aivis-source-detail-stat-divider" aria-hidden />
            <div className="aivis-source-detail-stat">
              <span className="aivis-source-detail-stat-label">Fit Score</span>
              <span className="aivis-source-detail-stat-value">
                {stats.relevance_check ? (
                  <span
                    className="aivis-sources-fit-badge"
                    style={{
                      background: (FIT_COLORS[stats.relevance_check] || {}).bg || 'var(--aivis-bg)',
                      color: (FIT_COLORS[stats.relevance_check] || {}).color || 'var(--aivis-text-muted)',
                    }}
                  >
                    {stats.relevance_check}
                  </span>
                ) : '—'}
              </span>
            </div>
            <div className="aivis-source-detail-stat-divider" aria-hidden />
            <div className="aivis-source-detail-stat">
              <span className="aivis-source-detail-stat-label">Sent to n8n</span>
              <span className="aivis-source-detail-stat-value">
                {stats.n8n_sent
                  ? <span className="aivis-sources-sent-badge aivis-sources-sent-badge--yes">Sent</span>
                  : <span className="aivis-sources-sent-badge aivis-sources-sent-badge--no">Not sent</span>}
              </span>
            </div>
            <div className="aivis-source-detail-stat-divider" aria-hidden />
            <div className="aivis-source-detail-stat">
              <span className="aivis-source-detail-stat-label">First seen</span>
              <span className="aivis-source-detail-stat-value">{absoluteDate(stats.first_seen)}</span>
            </div>
            <div className="aivis-source-detail-stat-divider" aria-hidden />
            <div className="aivis-source-detail-stat">
              <span className="aivis-source-detail-stat-label">Last seen</span>
              <span className="aivis-source-detail-stat-value">{relativeTime(stats.last_seen)}</span>
            </div>
          </div>
        </header>
      )}

      {/* ── Section 2: Charts ── */}
      <section className="aivis-dash-section" aria-label="Retrieval charts">
        <div className="aivis-dash-row aivis-source-detail-charts-row">
          {/* Line chart */}
          <div className="aivis-dash-card">
            <div className="aivis-dash-card-head">
              <h2 className="aivis-dash-card-title">Citations</h2>
              <p className="aivis-dash-card-desc">Number of times this URL appeared as a source per day.</p>
            </div>
            <div className="aivis-dash-chart-wrap aivis-dash-chart-wrap--tall">
              {lineData.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={lineData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={tokens.graphite200} vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke={tokens.graphite500} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke={tokens.graphite500} width={32} />
                    <Tooltip content={renderLineTooltip} />
                    <Line
                      type="monotone"
                      dataKey="retrievals"
                      name="Citations"
                      stroke={LINE_COLORS[0]}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="aivis-dash-empty">
                  {loading ? 'Loading…' : 'No citation data yet.'}
                </p>
              )}
            </div>
          </div>

          {/* Bar chart */}
          <div className="aivis-dash-card">
            <div className="aivis-dash-card-head">
              <h2 className="aivis-dash-card-title">Citations by model</h2>
              <p className="aivis-dash-card-desc">Number of times this URL was retrieved by each AI model.</p>
            </div>
            <div className="aivis-dash-chart-wrap aivis-dash-chart-wrap--tall">
              {barData.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={barData}
                    margin={{ top: 8, right: 16, left: 0, bottom: 52 }}
                    barCategoryGap="40%"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={tokens.graphite200} vertical={false} />
                    <XAxis
                      dataKey="service"
                      tick={<ServiceXTick />}
                      stroke={tokens.graphite500}
                      tickLine={false}
                      interval={0}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke={tokens.graphite500} width={32} />
                    <Tooltip content={renderBarTooltip} cursor={{ fill: 'rgba(23,15,73,0.04)' }} />
                    <Bar dataKey="count" name="Citations" radius={[6, 6, 0, 0]} maxBarSize={56}>
                      {barData.map((entry) => (
                        <Cell key={entry.service} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="aivis-dash-empty">
                  {loading ? 'Loading…' : 'No model data yet.'}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 3: Prompts table ── */}
      <section className="aivis-dash-section" aria-label="Prompts">
        <div className="aivis-dash-card">
          <div className="aivis-dash-card-head" style={{ marginBottom: 12 }}>
            <h2 className="aivis-dash-card-title">Prompts</h2>
            <p className="aivis-dash-card-desc">
              Prompts that cited this URL as a source — retrieval count and citation rate per prompt.
            </p>
          </div>

          <div className="aivis-domains-table-scroll">
            <table className="aivis-domains-table">
              <colgroup>
                <col style={{ width: '4%' }} />
                <col style={{ width: '78%' }} />
                <col style={{ width: '18%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className="aivis-domains-th-num">#</th>
                  <th scope="col">
                    <div className="aivis-domains-th-inner">
                      <span className="aivis-domains-th-label">Prompt</span>
                    </div>
                  </th>
                  <ColHeader label="Times Cited" sortKey="times_cited" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} tooltip={COL_TOOLTIPS.times_cited} numeric />
                </tr>
              </thead>
              <tbody>
                {pagePrompts.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="aivis-domains-empty">
                      {loading ? 'Loading…' : 'No prompt data yet.'}
                    </td>
                  </tr>
                ) : (
                  pagePrompts.map((row, idx) => (
                    <tr key={row.prompt_id} className="aivis-domains-tr">
                      <td className="aivis-domains-td-num aivis-domains-td-rank">
                        {(safePromptPage - 1) * PAGE_SIZE + idx + 1}
                      </td>
                      <td className="aivis-source-detail-prompt-td">
                        <span className="aivis-source-detail-prompt-text">{row.prompt_text || '—'}</span>
                      </td>
                      <td className="aivis-domains-td-num">{row.times_cited}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPrompts > PAGE_SIZE ? (
            <PaginationControls page={safePromptPage} totalPages={totalPromptPages} totalItems={totalPrompts} onPage={(p) => setPromptPage(p)} />
          ) : totalPrompts > 0 ? (
            <p className="aivis-domains-count">{totalPrompts} prompt{totalPrompts === 1 ? '' : 's'}</p>
          ) : null}
        </div>
      </section>

    </div>
  );
}
